import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";

import { EnabledFeaturesResponse, getEnabledFeatures } from "js/api/coursesConfig";
import { getLicensedFeatures, LicensedFeatures, LicensedFeaturesResponse } from "js/api/licensing";
import eventService, { EventStreamProvider } from "js/services/eventService";

import useStore from "stores/index";
import { useCoursesStore } from "stores/v2/coursesStore";
import { useLicenseStore } from "stores/v2/licenseStore";
import { ClasswizeTheme, useThemeStore } from "stores/v2/themeStore";
import { useUserSessionStore } from "stores/v2/userSessionStore";
import shallow from "zustand/shallow";

import FullPageLoader from "../FullPageLoader";
import { isCoursesEnabledForTeacher } from "../TabBar/utils/isCoursesEnabledForTeacher";
import { verifyAuth } from "js/api/authApi";
import { useClasswizeConfigStore } from "@/stores/classwizeConfigStore";
import { getClasswizeTenantKey } from "stores/classwizeConfigStore";
import { loadSignatures } from "@/query/signaturesQuery";
import { useQueryClient } from "@tanstack/react-query";

export const AuthenticatedRouteWrapper: FC = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [initialized, setInitialized] = useState(false);
	const initializingRef = useRef(false);

	const isAuthenticated = useUserSessionStore((state) => state.authenticated, shallow);
	const isAuthenticatedRef = useRef(isAuthenticated);

	const applianceInfo = useUserSessionStore((state) => state.deviceinfo, shallow);

	const userSessionStore = useUserSessionStore((state) => ({
		fetchDeviceInfo: state.fetchDeviceInfo,
		fetchUserInfo: state.fetchUserInfo,
		handleAuthenticationSuccess: state.handleAuthenticationSuccess,
		handleInvalidTokenError: state.handleInvalidTokenError,
	}));
	const licenseStore = useLicenseStore((state) => ({
		enableClassroom: state.enableClassroom,
		enableCourses: state.enableCourses,
		enableHallPass: state.enableHallPass,
		disableHallPass: state.disableHallPass,
	}));
	const coursesStore = useCoursesStore((state) => ({
		setShowCoursesTab: state.setShowCoursesTab,
	}));
	const themeStore = useThemeStore((state) => ({
		setTheme: state.setTheme,
	}));

	const logout = useCallback(() => {
		setInitialized(false);
		initializingRef.current = false;
		eventService.disconnect();

		// Redirect to tenant-specific login page if tenant key is available
		const tenantKey = getClasswizeTenantKey();
		if (tenantKey) {
			navigate(`/login/${tenantKey}`, { replace: true });
		} else {
			navigate("/login", { replace: true });
		}
	}, [navigate]);

	const init = useCallback(async () => {
		if (initialized) {
			return;
		}
		if (initializingRef.current) {
			console.debug("ignoring this init call as previous init is still running");
			return;
		}
		initializingRef.current = true;
		setInitialized(false);
		console.debug("running init");

		const stores = useStore.getState();
		try {
			const response = await verifyAuth();
			await userSessionStore.handleAuthenticationSuccess(response);
			await useClasswizeConfigStore.getState().fetch();
			await Promise.all([stores.featureToggles.fetch(), loadSignatures(queryClient)]);

			const [licensedFeaturesResponse, enabledFeaturesResponse] = await Promise.all([getLicensedFeatures(), getEnabledFeatures()]);

			const licensedFeatures = (await licensedFeaturesResponse.json()) as LicensedFeaturesResponse;
			if (licensedFeatures.features.includes(LicensedFeatures.ClassroomManagement)) {
				licenseStore.enableClassroom();
			}
			if (licensedFeatures.features.includes(LicensedFeatures.Courses)) {
				licenseStore.enableCourses();
			}
			if (licensedFeatures.features.includes(LicensedFeatures.HallPass)) {
				licenseStore.enableHallPass();
			} else {
				licenseStore.disableHallPass();
			}

			const enabledFeatures = (await enabledFeaturesResponse.json()) as EnabledFeaturesResponse;
			coursesStore.setShowCoursesTab(isCoursesEnabledForTeacher(enabledFeatures));

			const theme = applianceInfo?.customer_internal_company as ClasswizeTheme;
			themeStore.setTheme(theme);
			localStorage.setItem("zendesk_widget_theme", theme); // For Zendesk drawer to determine theme colour

			setInitialized(true);
			initializingRef.current = false;
			void eventService.connect();
		} catch (err) {
			console.error("failed fetching initial data,", err);
			userSessionStore.handleInvalidTokenError();
		} finally {
			initializingRef.current = false;
		}
	}, [applianceInfo?.customer_internal_company, userSessionStore, coursesStore, initialized, licenseStore, queryClient, themeStore]);

	useEffect(() => {
		if (isAuthenticated && !initialized) {
			init().catch(console.error);
		}

		const didLogout = !isAuthenticated && isAuthenticatedRef.current;
		if (didLogout) {
			logout();
		}
		isAuthenticatedRef.current = isAuthenticated;
	}, [init, initialized, isAuthenticated, logout]);

	if (!isAuthenticated) {
		// Redirect to tenant-specific login page if tenant key is available
		const tenantKey = getClasswizeTenantKey();
		if (tenantKey) {
			return <Navigate to={`/login/${tenantKey}`} replace />;
		}
		return <Navigate to="/login" replace />;
	} else if (!initialized) {
		return <FullPageLoader message="Loading Classroom Manager" />;
	} else {
		return (
			<EventStreamProvider>
				<Outlet />
			</EventStreamProvider>
		);
	}
};
